import { useEffect, useState } from 'react';
import apiService from '../services/apiService';

// One payment, found by its id.
//
// There is no GET-one-payment endpoint; the aggregate GET /bills/payments is all
// there is, so this walks it a page at a time until the id turns up. That walk is
// the whole reason the hook exists: the edit page used to call usePayments(1) —
// page 1 at a limit of 100 — while the list itself paginates 10 to a page, so
// opening any payment past row 100 rendered a blank form with nothing on screen
// to say why.
//
// `notFound` is deliberately a third answer alongside loading and error. "We
// looked and it isn't there" is not a failure, and the page has to be able to say
// so rather than showing empty inputs.
//
// getPayments swallows its own errors and can resolve to undefined, and the
// "No payments found" 404 body carries no `payments` key at all, so a missing or
// non-array `items` is read as an empty page that ends the walk.

const PAGE_SIZE = 200;
const MAX_PAGES = 50;

const matchesId = (payment, id) => {
  const candidate = payment?.id ?? payment?.Id;
  if (candidate === undefined || candidate === null) return false;
  return String(candidate) === String(id);
};

const usePayment = (paymentId) => {
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    // Navigating away mid-walk must not land state on an unmounted page.
    let cancelled = false;

    const findPayment = async () => {
      setPayment(null);
      setError(null);
      setNotFound(false);

      if (!paymentId) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        let found = null;
        for (let page = 1; page <= MAX_PAGES; page += 1) {
          const response = await apiService.getPayments(page, PAGE_SIZE);
          if (cancelled) return;

          const items = Array.isArray(response?.items) ? response.items : [];
          if (items.length === 0) break;

          found = items.find((item) => matchesId(item, paymentId)) || null;
          if (found) break;

          const totalPages = Number(response?.total_pages);
          if (Number.isFinite(totalPages) && page >= totalPages) break;
        }

        if (cancelled) return;
        if (found) setPayment(found);
        else setNotFound(true);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError(err.message || 'Error fetching payment');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    findPayment();
    return () => {
      cancelled = true;
    };
  }, [paymentId]);

  return { payment, loading, error, notFound };
};

export default usePayment;
